import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Box, Breadcrumbs, Container, Grid, Link, Typography, useTheme } from '@material-ui/core';
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import { sessionApi } from '../../../api/sessionApi';
import useMounted from '../../../hooks/useMounted';
import ChevronRightIcon from '../../../icons/ChevronRight';
import SessionFormationsFilter from './SessionFormationsFilter';
import gtm from '../../../lib/gtm';
import SessionFormationsResults, { applyFilters } from './SessionFormationsResults';
import SessionFormationsKanban from './SessionFormationsKanban';
import SessionFormationsAnalyse from './SessionFormationsAnalyse';
import { Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { darkVeil, getTone, toneAlpha, grey } from '../../../theme/tones';

const SessionFormationList = () => {
  const mounted = useMounted();
  const theme = useTheme();
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' ou 'tableau'
  const [filters, setFilters] = useState({
    searchQuery: '',
    status: null,
    format: null,
    actionType: null,
    profession: null,
    year: null,
    month: null,
    startDateMin: '',
    startDateMax: ''
  });
  const [sessionsFormation, setSessionsFormation] = useState([]);
  const [endDates, setEndDates] = useState([]);
  const [startDates, setStartDates] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);

  // remet filtres à zéro de src/components/dashboard/customer/CustomerListTable.js
  window.localStorage.setItem('choiceTabProfession', JSON.stringify('all'));
  window.localStorage.setItem('choiceSort', JSON.stringify('lastname|asc'));
  window.localStorage.setItem('choiceSource', JSON.stringify('all'));
  window.localStorage.setItem('choiceCampaign', JSON.stringify('all'));
  window.localStorage.setItem('choiceCallTrack', JSON.stringify('all'));
  window.localStorage.setItem('choiceCallTrackRelance', JSON.stringify('all'));
  window.localStorage.setItem('choiceAvancement', JSON.stringify('all'));

  useEffect(() => {
    gtm.push({ event: 'page_view' });
  }, []);

  // Par défaut le backend renvoie l'année + le mois courants (requête rapide).
  // Quand l'utilisateur change le filtre Année/Mois, on recharge côté serveur.
  const getSessionFormations = useCallback(async (year = null, month = null) => {
    try {
      const data = await sessionApi.getSessionFormations(
        localStorage.getItem('accessToken'),
        year,
        month
      );
      if (!data) {
        throw new Error('L\'session n\'existe pas');
      }

      if (mounted.current) {
        setSessionsFormation(data.sessionsFormation || []);
        setStartDates(data.startDates || []);
        setEndDates(data.endDates || []);
        if (data.availableYears) {
          setAvailableYears(data.availableYears);
        }
      }
    } catch (err) {
      console.error(err);
    }
  }, [mounted]);

  // Rechargement serveur dès que l'année ou le mois sélectionné change.
  useEffect(() => {
    getSessionFormations(filters.year, filters.month);
  }, [getSessionFormations, filters.year, filters.month]);

  return (
    <>
      <Helmet>
        <title>Formations Sessions | Syndays</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 8
        }}
      >
        <Container maxWidth={false} disableGutters sx={{ px: 2 }}>
          {/* ── Hero Header ── */}
          <Box
            sx={{
              mb: 4,
              p: 3.5,
              borderRadius: '24px',
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              backdropFilter: 'blur(20px)',
              boxShadow: theme.palette.mode === 'light'
                ? `0 4px 24px ${darkVeil(0.04)}`
                : `0 4px 24px ${darkVeil(0.2)}`,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '16px',
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: (theme) => `0 8px 20px ${alpha(theme.palette.secondary.main, 0.3)}`,
                  flexShrink: 0,
                }}
              >
                <FilterNoneIcon sx={{ color: grey(0), fontSize: '1.7rem' }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.5rem',
                    color: 'text.primary',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                  }}
                >
                  Liste des sessions
                </Typography>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<ChevronRightIcon fontSize="small" />}
                  sx={{ mt: 1 }}
                >
                  <Link
                    color="textSecondary"
                    component={RouterLink}
                    to="/dashboard"
                    variant="subtitle2"
                    sx={{ textDecoration: 'none', '&:hover': { color: 'secondary.main' } }}
                  >
                    Tableau de bord
                  </Link>
                  <Link
                    color="textSecondary"
                    component={RouterLink}
                    to="/dashboard/formationSession"
                    variant="subtitle2"
                    sx={{ textDecoration: 'none', '&:hover': { color: 'secondary.main' } }}
                  >
                    Sessions
                  </Link>
                  <Typography
                    color="textPrimary"
                    variant="subtitle2"
                    sx={{ fontWeight: 600 }}
                  >
                    Liste
                  </Typography>
                </Breadcrumbs>
              </Box>
            </Box>

            {/* Sélecteur de vue Kanban / Tableau */}
            <Box
              sx={{
                display: 'inline-flex',
                gap: 0.5,
                backgroundColor: 'divider',
                p: 0.5,
                borderRadius: '12px',
                border: '1px solid',
                borderColor: 'divider',
                width: { xs: '100%', md: 'auto' },
                justifyContent: 'center'
              }}
            >
              <Button
                onClick={() => setViewMode('kanban')}
                sx={{
                  px: 3,
                  py: 0.75,
                  borderRadius: '9px',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  minWidth: '100px',
                  color: viewMode === 'kanban' ? grey(0) : 'text.secondary',
                  backgroundColor: viewMode === 'kanban' ? getTone('blue').base : 'transparent',
                  boxShadow: viewMode === 'kanban' ? `0 4px 12px ${toneAlpha('blue', 0.2)}` : 'none',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: viewMode === 'kanban' ? getTone('blue').color : darkVeil(0.03)
                  }
                }}
              >
                Kanban
              </Button>
              <Button
                onClick={() => setViewMode('tableau')}
                sx={{
                  px: 3,
                  py: 0.75,
                  borderRadius: '9px',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  minWidth: '100px',
                  color: viewMode === 'tableau' ? grey(0) : 'text.secondary',
                  backgroundColor: viewMode === 'tableau' ? getTone('blue').base : 'transparent',
                  boxShadow: viewMode === 'tableau' ? `0 4px 12px ${toneAlpha('blue', 0.2)}` : 'none',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: viewMode === 'tableau' ? getTone('blue').color : darkVeil(0.03)
                  }
                }}
              >
                Tableau
              </Button>
              <Button
                onClick={() => setViewMode('analyse')}
                sx={{
                  px: 3,
                  py: 0.75,
                  borderRadius: '9px',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  minWidth: '100px',
                  color: viewMode === 'analyse' ? grey(0) : 'text.secondary',
                  backgroundColor: viewMode === 'analyse' ? getTone('blue').base : 'transparent',
                  boxShadow: viewMode === 'analyse' ? `0 4px 12px ${toneAlpha('blue', 0.2)}` : 'none',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: viewMode === 'analyse' ? getTone('blue').color : darkVeil(0.03)
                  }
                }}
              >
                Analyse
              </Button>
            </Box>
          </Box>
          <Box sx={{ mt: 3 }}>
            <SessionFormationsFilter
              endDates={endDates}
              startDates={startDates}
              setFilters={setFilters}
              sessionsFormation={sessionsFormation}
              availableYears={availableYears}
            />
          </Box>
          <Box sx={{ mt: 6 }}>
            {viewMode === 'tableau' && (
              <SessionFormationsResults
                sessionsFormation={sessionsFormation}
                filters={filters}
                setSessionsFormation={setSessionsFormation}
              />
            )}
            {viewMode === 'kanban' && (
              <SessionFormationsKanban
                filteredSessions={applyFilters(sessionsFormation, filters)}
                setSessionsFormation={setSessionsFormation}
              />
            )}
            {viewMode === 'analyse' && (
              <SessionFormationsAnalyse
                sessions={applyFilters(sessionsFormation, filters)}
              />
            )}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default SessionFormationList;
